import type { HttpClient } from '../http/HttpClient'
import { backendStatus } from '../http/errorDetail'
import { ConnectorConsentError } from '@/domain/connector/ConnectorConsent'
import type {
  ConnectorConsent,
  ConnectorConsentFailure,
  ConnectorConsentRepository,
  ConnectorDecision,
} from '@/domain/connector/ConnectorConsent'

interface RawConnectorConsent {
  client_name: string
  client_host: string
  society: { id: number, name: string }
  companies: { id: number, name: string }[]
  email: string
  eligible: boolean
}

export function toConnectorConsent(raw: RawConnectorConsent): ConnectorConsent {
  return {
    clientName: raw.client_name,
    clientHost: raw.client_host,
    society: raw.society,
    companies: raw.companies,
    email: raw.email,
    eligible: raw.eligible,
  }
}

function toConsentError(error: unknown): ConnectorConsentError {
  const failures: Record<number, ConnectorConsentFailure> = { 400: 'invalid_flow', 403: 'not_allowed' }
  return new ConnectorConsentError(failures[backendStatus(error) ?? 0] ?? 'unavailable')
}

export class HttpConnectorConsentRepository implements ConnectorConsentRepository {
  constructor(private http: HttpClient) {}

  async get(flow: string): Promise<ConnectorConsent> {
    try {
      return toConnectorConsent(await this.http.url('connector/consent').params({ flow }).get<RawConnectorConsent>())
    } catch (error) {
      throw toConsentError(error)
    }
  }

  async decide(flow: string, decision: ConnectorDecision): Promise<string> {
    try {
      const { redirect } = await this.http.url('connector/consent').body({ flow, decision }).post<{ redirect: string }>()
      return redirect
    } catch (error) {
      throw toConsentError(error)
    }
  }

  async disconnect(): Promise<void> {
    try {
      await this.http.url('connector/revoke').post()
    } catch (error) {
      throw toConsentError(error)
    }
  }
}
